// src/components/UserMeet/BreakoutInviteModal.jsx
import React, { useEffect, useState } from "react";
import Icon from "@/pages/MeetingRoom/ui/Icon";

const AVATAR_GRADIENTS = [
  ["#4ADE80", "#16A34A"],
  ["#38BDF8", "#0284C7"],
  ["#FCD34D", "#D97706"],
  ["#C084FC", "#7C3AED"],
];

const getAvatarColors = (name = "") => {
  if (!name) return AVATAR_GRADIENTS[0];
  return AVATAR_GRADIENTS[name.charCodeAt(0) % AVATAR_GRADIENTS.length];
};

export default function BreakoutInviteModal({
  open,
  invite,
  onAccept,
  onDecline,
  loading,
}) {
  const [timeLeft, setTimeLeft] = useState(30);

  // Auto-dismiss countdown (30 seconds)
  useEffect(() => {
    if (!open) return;
    setTimeLeft(30);

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onDecline?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  if (!open || !invite) return null;

  const [c1, c2] = getAvatarColors(invite.ownerName);

  return (
    <div
      className="jt-modalOverlay"
      role="dialog"
      aria-modal="true"
      style={{ zIndex: 10000 }}
    >
      <div
        className="jt-sheet"
        style={{ maxWidth: 400 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="jt-sheetHead">
          <div className="jt-sheetTitle">
            <span style={{ marginRight: 8 }}>🔒</span>
            Private Meeting Invite
          </div>
          <button className="jt-sheetClose" onClick={onDecline}>
            <Icon name="close" />
          </button>
        </div>

        {/* Body */}
        <div className="jt-sheetBody">
          {/* Inviter Info */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "16px",
              borderRadius: 14,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              marginBottom: 20,
            }}
          >
            {/* Avatar */}
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${c1}, ${c2})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
                fontWeight: 700,
                color: "#fff",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {invite.ownerImage ? (
                <img
                  src={invite.ownerImage}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                (invite.ownerName?.[0] || "?").toUpperCase()
              )}
            </div>

            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {invite.ownerName}
              </div>
              <div style={{ opacity: 0.7, fontSize: 13, marginTop: 2 }}>
                is inviting you to a private meeting
              </div>
            </div>
          </div>

          {/* Meeting Details */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                padding: "12px",
                borderRadius: 12,
                background: "rgba(74,222,128,0.08)",
                border: "1px solid rgba(74,222,128,0.2)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 4 }}>⏱️</div>
              <div style={{ fontWeight: 700, color: "#4ADE80" }}>
                {invite.timeLimitMinutes} min
              </div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Duration</div>
            </div>

            <div
              style={{
                padding: "12px",
                borderRadius: 12,
                background: "rgba(74,222,128,0.08)",
                border: "1px solid rgba(74,222,128,0.2)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 4 }}>🔒</div>
              <div style={{ fontWeight: 700, color: "#4ADE80" }}>Private</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Invite Only</div>
            </div>
          </div>

          {/* Auto-dismiss timer */}
          <div
            style={{
              textAlign: "center",
              fontSize: 13,
              opacity: 0.6,
              marginBottom: 20,
            }}
          >
            Auto-dismisses in{" "}
            <span style={{ color: "#FCD34D", fontWeight: 700 }}>
              {timeLeft}s
            </span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              onClick={onDecline}
              disabled={loading}
              style={{
                flex: 1,
                padding: "13px 0",
                borderRadius: 12,
                border: "1px solid rgba(251,113,133,0.4)",
                background: "rgba(251,113,133,0.1)",
                color: "#FB7185",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              ✕ Decline
            </button>

            <button
              type="button"
              onClick={onAccept}
              disabled={loading}
              style={{
                flex: 2,
                padding: "13px 0",
                borderRadius: 12,
                border: "none",
                background: loading
                  ? "rgba(74,222,128,0.4)"
                  : "linear-gradient(135deg, #4ADE80, #16A34A)",
                color: "#062317",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: 700,
                fontSize: 15,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {loading ? (
                <>
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      border: "2px solid #062317",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  Joining...
                </>
              ) : (
                "✓ Accept"
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}